import { CARD_VALUES } from '@/config/cards';
import type { CardDef } from './cards';

/**
 * Trade-off and red-core cards (docs/03, docs/12 C2) — the tail of the
 * card pool. Order matters: CARDS spreads this list at its end and the
 * seeded offer rolls walk the pool in order.
 */
export const SPECIAL_CARDS: ReadonlyArray<CardDef> = [
  // ---- Trade-offs (docs/12 C2): one stat up (green), another down (red).
  // The boost is roughly double a plain card — the downside is the price.
  {
    id: 'glasscannon', icon: 'damage', tradeoff: { up: 'damage', down: 'iframes' },
    kind: 'offense', rarity: 'rare', currency: 'yellow', maxStacks: 2,
    nameKey: 'card.glasscannon.name', descKey: 'card.glasscannon.desc',
    apply: ({ stats }) => {
      stats.damageMul *= CARD_VALUES.glasscannon.damage;
      stats.iframeMul *= CARD_VALUES.glasscannon.iframes;
    },
  },
  {
    id: 'hairtrigger', icon: 'firerate', tradeoff: { up: 'firerate', down: 'range' },
    kind: 'offense', rarity: 'uncommon', currency: 'yellow', maxStacks: 3,
    nameKey: 'card.hairtrigger.name', descKey: 'card.hairtrigger.desc',
    apply: ({ stats }) => {
      stats.fireRateMul *= CARD_VALUES.hairtrigger.fireRate;
      stats.rangeMul *= CARD_VALUES.hairtrigger.range;
    },
  },
  {
    id: 'heavyplating', icon: 'shieldstart', tradeoff: { up: 'shieldstart', down: 'speed' },
    kind: 'defense', rarity: 'rare', currency: 'yellow', maxStacks: 2,
    nameKey: 'card.heavyplating.name', descKey: 'card.heavyplating.desc',
    apply: ({ stats }) => {
      stats.shieldStart += 1;
      stats.speedMul *= CARD_VALUES.heavyplating.speed;
    },
  },
  {
    id: 'boosterrig', icon: 'speed', tradeoff: { up: 'speed', down: 'damage' },
    kind: 'mobility', rarity: 'uncommon', currency: 'yellow', maxStacks: 3,
    nameKey: 'card.boosterrig.name', descKey: 'card.boosterrig.desc',
    apply: ({ stats }) => {
      stats.speedMul *= CARD_VALUES.boosterrig.speed;
      stats.damageMul *= CARD_VALUES.boosterrig.damage;
    },
  },
  {
    id: 'lootballast', icon: 'greed', tradeoff: { up: 'greed', down: 'speed' },
    kind: 'economy', rarity: 'uncommon', currency: 'yellow', maxStacks: 3,
    nameKey: 'card.lootballast.name', descKey: 'card.lootballast.desc',
    apply: ({ stats }) => {
      stats.greedMul *= CARD_VALUES.lootballast.greed;
      stats.speedMul *= CARD_VALUES.lootballast.speed;
    },
  },

  // ---- Epic (red)
  {
    id: 'rack', icon: 'rack', kind: 'special', rarity: 'epic', currency: 'red',
    nameKey: 'card.rack.name', descKey: 'card.rack.desc', maxStacks: 2,
    canOffer: ({ weapon }) => weapon.rackSlots < 2,
    apply: ({ weapon }) => { weapon.rackSlots = Math.min(2, weapon.rackSlots + 1); },
  },
  {
    id: 'ghostdash', icon: 'ghostdash', kind: 'mobility', rarity: 'epic', currency: 'red',
    nameKey: 'card.ghostdash.name', descKey: 'card.ghostdash.desc', maxStacks: 2,
    apply: ({ stats }) => { stats.dashIframeBonus += CARD_VALUES.ghostIframes; },
  },
  {
    id: 'superfan', icon: 'superfan', kind: 'offense', rarity: 'epic', currency: 'red',
    nameKey: 'card.superfan.name', descKey: 'card.superfan.desc',
    apply: ({ stats }) => { stats.superFanBonus += 2; },
  },
  // ---- Legendary / Mythic (red): build-defining, very rare
  {
    id: 'lifesteal', icon: 'lifesteal', kind: 'defense', rarity: 'legendary', currency: 'red',
    nameKey: 'card.lifesteal.name', descKey: 'card.lifesteal.desc', maxStacks: 2,
    apply: ({ stats }) => { stats.lifestealPct += CARD_VALUES.lifesteal; },
  },
  {
    id: 'apex', icon: 'critdamage', kind: 'offense', rarity: 'mythic', currency: 'red',
    nameKey: 'card.apex.name', descKey: 'card.apex.desc', maxStacks: 1,
    apply: ({ stats }) => {
      stats.critChanceBonus += CARD_VALUES.apexCritChance;
      stats.critDamageBonus += CARD_VALUES.apexCritDamage;
    },
  },
  {
    // The only big all-damage piece in the pool — kept VERY rare on purpose
    id: 'overcharge', icon: 'overcharge', kind: 'offense', rarity: 'legendary', currency: 'red',
    weight: 0.6,
    nameKey: 'card.overcharge.name', descKey: 'card.overcharge.desc',
    apply: ({ stats }) => { stats.damageMul *= CARD_VALUES.overcharge; },
  },
];
